import {combineLatest, BehaviorSubject, Subject, withLatestFrom, ReplaySubject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {ga} from '@px/da/ga';
import {ty} from '@px/da/tingyun';
import {env, envUtil} from '@px/helper/env';
import {mc} from '@px/helper/mc';
import {appPlatform$, appToken$} from '@px/helper/bridge';
import {userApi} from '@px/api/user';
import {readToken, removeToken, writeToken} from '@px/storage/token';
import {readLanguage, writeLanguage} from '@px/storage/lang';
import {updateUsedUIDs} from '@px/storage/uids';
import {loadPreference, resetPreference} from './preference';
import {userTags$, resetUserInfo, userInfo$, userMemberLevel$, isSubAccount$} from './user';
import {fiat$} from './fiat';
import {wsOpened$} from './ws';
import {wwData$} from './worker-data';
import {isActivateMarginAccount$, isActivateLendingAccount$} from './config';
import {lang$} from "./lang";

// Trade 没有 SSG，所有不再给出 Init 状态
export const authStatus$ = new BehaviorSubject<AuthStatus>('Logout');
export const isLogin$ = new BehaviorSubject(false);
// 避免 import 行为引发 http 请求报错
setTimeout(function initAuthStatus() {
  const isLogin = !!readToken();
  setDifferentStatus(authStatus$, isLogin ? 'Login' : 'Logout');
}, 0);

export const login$ = new Subject<UserInfo>();
export const logout$ = new Subject<{manual?: boolean; code?: number}>();
export const disabledLanguages$ = new BehaviorSubject([]);
export const afterLogin$ = new ReplaySubject<void>(1);

mc.subscribe('user', 'logout', () => logout$.next({manual: true}));
mc.subscribe('http', '401', () => logout$.next({manual: false}));

function setDifferentStatus<T>(stream$: BehaviorSubject<T>, val: T) {
  const oldVal = stream$.getValue();
  if (oldVal !== val) {
    stream$.next(val);
  }
}

authStatus$.pipe(map((authStatus) => authStatus === 'Login')).subscribe((o) => {
  setDifferentStatus(isLogin$, o);
});

login$.subscribe((userInfo: UserInfo) => {
  // 先更新数据，后更新登录状态
  const {userId, parentId} = userInfo;
  ga.bindUserId(userId, parentId);
  ty.setUid(userId);
  userInfo$.next(userInfo);
  lang$.next(userInfo.lang as Language)
  // 要过滤业务
  wwData$.next({
    type: 'sub-users-subscribe',
    users: {
      main: userInfo,
      subs: userInfo.subUsers || [],
    },
  });

  wwData$.next({
    type: 'init-user-data',
    data: userInfo,
  });

  loadPreference().then();
  fiat$.next(userInfo.currency || 'USD');
  localStorage.setItem('current-user-id', String(userId));
  updateUsedUIDs(userInfo.userId);
  getUserMemberLevel().then();

  const isActivateMarginAccount = (!userInfo?.uta || userInfo.uta.spot !== 2) && userInfo.marginTradingAccountInfo && userInfo.marginTradingAccountInfo.marginAccountStatus === 1; // 0:未开户 1:正常使用
  isActivateMarginAccount$.next(isActivateMarginAccount);
  setDifferentStatus(authStatus$, 'Login');
  isActivateLendingAccount$.next(userInfo.lendingAccount && userInfo.lendingAccount.status === 1); // 0:未开户 1:正常使用

  afterLogin$.next();
});

logout$.subscribe(async ({manual, code}) => {
  // 先设置登录状态，后清空数据
  setDifferentStatus(authStatus$, 'Logout');
  resetUserInfo();
  resetPreference();

  fiat$.next('USD');

  if (manual) {
    await userApi.logout();
  }
  removeToken();

  disabledLanguages$.subscribe((disabledLanguages) => {
    if (disabledLanguages.indexOf(readLanguage()) > -1) {
      writeLanguage('en');
    }
  });

  if (env.isApp) {
    return;
  }
  // 3401 token 非法，走补偿逻辑 => 直接刷新页面，以非登录态尝试访问
  if (manual || code === 3401) {
    window.location.reload();
  }
});

/**
 * 登录后只要 ws open (包括断线重连)，就需要重新认证
 */
combineLatest([isLogin$, wsOpened$]).subscribe(doWsAuth);

wwData$
  .pipe(filter((o) => o.type === 'auth-error'))
  .pipe(
    map((o) => o.error?.code),
    filter((code) => code === 6012) // "invalid login token"
  )
  .pipe(switchMap(() => checkAuth()))
  .subscribe();

wwData$
  .pipe(filter((o) => o.type === 'auth-timeout'))
  .subscribe(() => location.reload());

authStatus$
  .pipe(
    filter((authStatus) => authStatus === 'Login'),
    switchMap(() => getTag())
  )
  .subscribe();

authStatus$
  .pipe(
    filter((authStatus) => authStatus !== 'Init'),
    withLatestFrom(userInfo$),
    map(([, userInfo]) => userInfo.isSub)
  )
  .subscribe((isSub) => {
    isSubAccount$.next(isSub);
  });

appToken$.pipe(distinctUntilChanged()).subscribe((appToken) => {
  writeToken(appToken);
  setTimeout(() => {
    checkAuth();
  }, 0);
});

export async function checkAuth() {
  const token = readToken();
  if (!token) {
    authStatus$.next('Logout');
    return;
  }

  const {data, error} = await userApi.info();
  if (data) {
    login$.next(data);
  }
  if (error && error.code === 3401) {
    logout$.next({manual: false, code: error.code});
    console.warn('Token invalid', error);
  }
}

function doWsAuth() {
  wwData$.next({type: 'token-update', platform: readPlatform(), token: readToken()});
}

function readPlatform() {
  if (envUtil.isApp) {
    return appPlatform$.getValue().toUpperCase();
  }
  return 'PC';
}

async function getTag() {
  const {data} = await userApi.getTag();
  if (data) {
    userTags$.next(data);
  }
}

async function getUserMemberLevel() {
  const {data} = await userApi.getUserMemberLevel();
  if (data) {
    userMemberLevel$.next(data.memberLevel);
  }
}
